import { PageNotice } from '../../../shared/components/PageFeedback';
import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import {
  CreateInvoiceDialog,
  CreateImmediatePaymentDialog,
  InvoicesTable,
  PaymentsTable,
} from '@/modules/accounting/components';
import { getIncomeMetricConfig } from '../components/shared/accountingPage.config';
import { useAccountingIncomeData, useCreateInvoiceForm } from '@/modules/accounting/hooks';
import {
  ACCOUNTING_PERIOD_OPTIONS,
  PAYMENT_TYPE_FILTER_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
} from '@/modules/accounting/utils';
import AdaptiveFilterBar from '../../../shared/components/patterns/AdaptiveFilterBar';
import MetricGrid from '../../../shared/components/patterns/MetricGrid';
import PageFrame from '../../../shared/components/patterns/PageFrame';
import PageHeader from '../../../shared/components/patterns/PageHeader';
import PrimaryActionButton from '../../../shared/components/patterns/PrimaryActionButton';
import ConfirmActionDialog from '../../../shared/components/patterns/ConfirmActionDialog';
import PageActionRow from '../../../shared/components/patterns/PageActionRow';
import PageDivider from '../../../shared/components/patterns/PageDivider';

import '../styles/accounting.css';
import { parseEntityDeepLink, clearEntityDeepLink } from '../../../shared/utils/entityDeepLink';

export default function AccountingPage() {
  const { capabilities } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedPrefillLocationKey = useRef(null);
  const [activeLedger, setActiveLedger] = useState('payments');
  const [voidInvoiceRequest, setVoidInvoiceRequest] = useState(null);
  const [deletePaymentRequest, setDeletePaymentRequest] = useState(null);
  const [immediatePaymentOpen, setImmediatePaymentOpen] = useState(false);
  const clientIdParam = searchParams.get('clientId') || '';
  const periodFromParam = searchParams.get('periodFrom') || '';
  const periodToParam = searchParams.get('periodTo') || '';
  const deepLink = parseEntityDeepLink(searchParams, { paymentId: 'payment', invoiceId: 'invoice' });
  const focusedPaymentId = deepLink.entityType === 'payment' ? deepLink.entityId : '';
  const focusedInvoiceId = deepLink.entityType === 'invoice' ? deepLink.entityId : '';
  const incomeData = useAccountingIncomeData({
    initialClientId: clientIdParam,
    periodFrom: periodFromParam,
    periodTo: periodToParam,
  });
  const withInstallments = (items = []) => items.map((invoice) => ({ ...invoice, installments: (incomeData.payments || []).filter((payment) => String(payment.invoiceId) === String(invoice.id)) }));
  const moduleFilters = useRegisteredModuleFilters(activeLedger === 'invoices' ? 'invoices' : 'payments', activeLedger === 'invoices' ? withInstallments(incomeData.invoices) : incomeData.payments);
  const filteredPayments = activeLedger === 'payments' ? moduleFilters.filterRows(incomeData.filteredPayments) : incomeData.filteredPayments;
  const filteredInvoices = activeLedger === 'invoices' ? moduleFilters.filterRows(withInstallments(incomeData.filteredInvoices)) : incomeData.filteredInvoices;
  const invoiceForm = useCreateInvoiceForm({
    clients: incomeData.clients,
    onCreate: incomeData.createInvoiceResult,
  });
  const accountingModuleDisabled = incomeData.incomeCatalogStatus === 'module_disabled';
  const accountingTenantDatabaseError = incomeData.incomeCatalogStatus === 'tenant_database_error';
  const visiblePayments = focusedPaymentId
    ? filteredPayments.filter((payment) => String(payment.id) === String(focusedPaymentId))
    : filteredPayments;

  useEffect(() => {
    if (focusedPaymentId) setActiveLedger('payments');
    if (focusedInvoiceId) setActiveLedger('invoices');
  }, [focusedInvoiceId, focusedPaymentId]);

  useEffect(() => {
    if (periodFromParam || periodToParam) incomeData.setFilter('selectedPeriod', 'dashboard_period');
  }, [incomeData.setFilter, periodFromParam, periodToParam]);

  useEffect(() => {
    if (deepLink.mode !== 'delete') return;
    if (focusedInvoiceId) {
      const invoice = incomeData.invoices.find((item) => String(item.id) === String(focusedInvoiceId));
      if (invoice) setVoidInvoiceRequest(invoice);
    }
    if (focusedPaymentId) {
      const payment = incomeData.payments.find((item) => String(item.id) === String(focusedPaymentId));
      if (payment) setDeletePaymentRequest(payment);
    }
  }, [deepLink.mode, focusedInvoiceId, focusedPaymentId, incomeData.invoices, incomeData.payments]);

  useEffect(() => {
    const createInvoicePrefill = location.state?.createInvoicePrefill;

    if (!createInvoicePrefill || consumedPrefillLocationKey.current === location.key) {
      return;
    }

    consumedPrefillLocationKey.current = location.key;
    invoiceForm.openDialog(createInvoicePrefill);
    navigate(location.pathname, { replace: true, state: null });
  }, [invoiceForm, location.key, location.pathname, location.state, navigate]);

  const filterConfig = {
    label: 'Filtri contabilità',
    search: {
      key: 'query',
      placeholder: 'Cerca incassi o fatture...',
    },
    groups: [
      {
        key: 'selectedClient',
        label: 'Cliente',
        selection: 'single',
        emptyValue: 'tutti',
        options: [
          { value: 'tutti', label: 'Tutti i clienti' },
          ...incomeData.clients.map((client) => ({ value: String(client.id), label: client.name })),
        ],
      },
      {
        key: 'selectedPeriod',
        label: 'Periodo',
        selection: 'single',
        emptyValue: 'tutti',
        options: [
          ...ACCOUNTING_PERIOD_OPTIONS,
          ...(periodFromParam || periodToParam
            ? [{ value: 'dashboard_period', label: 'Periodo Dashboard' }]
            : []),
        ],
      },
      {
        key: 'selectedStatus',
        label: 'Stato',
        selection: 'single',
        emptyValue: 'tutti',
        options: PAYMENT_STATUS_OPTIONS,
      },
      {
        key: 'selectedPaymentType',
        label: 'Tipologia pagamento',
        selection: 'single',
        emptyValue: 'tutti',
        options: PAYMENT_TYPE_FILTER_OPTIONS,
      },
    ],
  };
  const selectedClient = incomeData.clients.find(
    (client) => String(client.id) === String(incomeData.filters.selectedClient)
  );

  function clearClientContext() {
    incomeData.setFilter('selectedClient', 'tutti');
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('clientId');
    setSearchParams(nextParams, { replace: true });
  }

  function resetFilters() {
    incomeData.setFilter('query', '');
    incomeData.setFilter('selectedClient', 'tutti');
    incomeData.setFilter('selectedPeriod', 'tutti');
    incomeData.setFilter('selectedStatus', 'tutti');
    incomeData.setFilter('selectedPaymentType', 'tutti');
    incomeData.clearOverviewFilter();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('clientId');
    nextParams.delete('paymentId');
    nextParams.delete('periodFrom');
    nextParams.delete('periodTo');
    nextParams.delete('metric');
    setSearchParams(nextParams, { replace: true });
  }

  return (
    <PageFrame filterModel={moduleFilters} className="accounting-page">
      <PageHeader title="Contabilità" description="Gestione centralizzata di fatture, incassi e scadenze" />

      {incomeData.loadError ? (
        <PageNotice role="alert" className="accounting-alert accounting-alert--warning wt-inline-error">{incomeData.loadError}</PageNotice>
      ) : null}

      {!accountingModuleDisabled && !accountingTenantDatabaseError ? (
        <>
          {incomeData.filters.selectedClient !== 'tutti' ? (
            <div className="accounting-alert">
              <span>
                Vista filtrata per cliente:{' '}
                {selectedClient?.name || `Cliente ${incomeData.filters.selectedClient}`}.
              </span>
              <button type="button" className="accounting-inline-button" onClick={clearClientContext}>
                Mostra tutti
              </button>
            </div>
          ) : null}
          {focusedPaymentId ? (
            <div className="accounting-alert" role="status">
              <span>Vista focalizzata sull’incasso #{focusedPaymentId}.</span>
              <button
                type="button"
                className="accounting-inline-button"
                onClick={() => {
                  const nextParams = new URLSearchParams(searchParams);
                  nextParams.delete('paymentId');
                  setSearchParams(nextParams, { replace: true });
                }}
              >
                Mostra tutti
              </button>
            </div>
          ) : null}

          <MetricGrid
            metrics={getIncomeMetricConfig(incomeData.stats).map((metric) => ({
              ...metric,
              active: incomeData.activeOverviewFilter === metric.id,
              onClick: () => {
                setActiveLedger('payments');
                incomeData.setOverviewFilter(metric.id);
              },
            }))}
          />

          <PageDivider />
          <AdaptiveFilterBar
            config={filterConfig}
            values={incomeData.filters}
            onChange={(key, value) => incomeData.setFilter(key, value)}
            onReset={resetFilters}
            filtersLabel="Filtri contabilità"
          />
          <PageDivider />
          <PageActionRow>
            <PrimaryActionButton
              onClick={() => invoiceForm.openDialog()}
              disabled={
                incomeData.clients.length === 0 || accountingModuleDisabled || accountingTenantDatabaseError
              }
              iconKey="actions.add"
            >
              Nuova
            </PrimaryActionButton>
            <PrimaryActionButton onClick={() => setImmediatePaymentOpen(true)} iconKey="actions.add">
              Incasso immediato
            </PrimaryActionButton>
          </PageActionRow>

          <section className="accounting-ledger-section">
            <div className="accounting-ledger-section__header">
              <h2>Registro contabile</h2>
              <p>
                {activeLedger === 'payments'
                  ? `${visiblePayments.length} incassi filtrati`
                  : `${filteredInvoices.length} fatture filtrate`}
              </p>
            </div>
            {activeLedger === 'payments' ? (
              <PaymentsTable
                views={[
                  {
                    id: 'payments',
                    label: 'Incassi',
                    count: visiblePayments.length,
                    iconKey: 'metrics.currency',
                    tone: 'success',
                  },
                  {
                    id: 'invoices',
                    label: 'Fatture',
                    count: filteredInvoices.length,
                    iconKey: 'metrics.document',
                    tone: 'info',
                  },
                ]}
                activeViewId={activeLedger}
                onViewChange={setActiveLedger}
                payments={visiblePayments}
                allPayments={incomeData.payments}
                invoices={incomeData.invoices}
                clients={incomeData.clients}
                onRegisterPayment={incomeData.registerPayment}
                onReversePayment={incomeData.reversePayment}
                onRestorePayment={incomeData.restorePayment}
                onCancelPayment={setDeletePaymentRequest}
                onUpdatePayment={incomeData.updatePayment}
                onUpdateInvoice={incomeData.updateInvoice}
                canManageAccountingDestructiveActions={
                  capabilities?.canManageAccountingDestructiveActions === true
                }
                focusedEntityId={focusedPaymentId}
                focusedMode={deepLink.mode}
                onClearFocus={() =>
                  setSearchParams(clearEntityDeepLink(searchParams, ['paymentId', 'invoiceId']), {
                    replace: true,
                  })
                }
              />
            ) : (
              <InvoicesTable
                onUpdateInvoice={incomeData.updateInvoice}
                views={[
                  {
                    id: 'payments',
                    label: 'Incassi',
                    count: visiblePayments.length,
                    iconKey: 'metrics.currency',
                    tone: 'success',
                  },
                  {
                    id: 'invoices',
                    label: 'Fatture',
                    count: filteredInvoices.length,
                    iconKey: 'metrics.document',
                    tone: 'info',
                  },
                ]}
                activeViewId={activeLedger}
                onViewChange={setActiveLedger}
                invoices={
                  focusedInvoiceId
                    ? filteredInvoices.filter(
                        (item) => String(item.id) === String(focusedInvoiceId)
                      )
                    : filteredInvoices
                }
                payments={incomeData.payments}
                onUpdatePayment={incomeData.updatePayment}
                onRegisterPayment={incomeData.registerPayment}
                onVoidInvoice={(invoiceId) => setVoidInvoiceRequest({ id: invoiceId })}
                canManageAccountingDestructiveActions={
                  capabilities?.canManageAccountingDestructiveActions === true
                }
                focusedEntityId={focusedInvoiceId}
                focusedMode={deepLink.mode}
                onClearFocus={() =>
                  setSearchParams(clearEntityDeepLink(searchParams, ['paymentId', 'invoiceId']), {
                    replace: true,
                  })
                }
              />
            )}
          </section>

          <CreateInvoiceDialog
            open={invoiceForm.isOpen}
            onOpenChange={(open) => (open ? invoiceForm.openDialog() : invoiceForm.closeDialog())}
            clients={incomeData.clients}
            formState={{
              form: invoiceForm.form,
              installments: invoiceForm.installments,
              prefillClientWarning: invoiceForm.prefillClientWarning,
              isSubmitting: invoiceForm.isSubmitting,
              clientsUnavailable: incomeData.clientsUnavailable,
              onFieldChange: invoiceForm.updateField,
              onClientChange: invoiceForm.handleClientChange,
              onAddInstallment: invoiceForm.addInstallment,
              onRemoveInstallment: invoiceForm.removeInstallment,
              onUpdateInstallment: invoiceForm.updateInstallment,
              onCancel: invoiceForm.closeDialog,
              onSubmit: invoiceForm.submit,
            }}
          />
          <CreateImmediatePaymentDialog
            open={immediatePaymentOpen}
            onOpenChange={setImmediatePaymentOpen}
            clients={incomeData.clients}
            onCreated={incomeData.immediatePaymentCreated}
          />
        </>
      ) : null}

      <ConfirmActionDialog
        open={Boolean(voidInvoiceRequest)}
        title="Annullare questa fattura?"
        description="La fattura verrà annullata e gli incassi previsti collegati saranno chiusi."
        confirmLabel="Annulla fattura"
        cancelLabel="Indietro"
        tone="danger"
        onCancel={() => setVoidInvoiceRequest(null)}
        onConfirm={async () => {
          const result = await incomeData.voidInvoice(voidInvoiceRequest.id);
          if (result?.ok) {
            setVoidInvoiceRequest(null);
            setSearchParams(clearEntityDeepLink(searchParams, ['invoiceId']), { replace: true });
          }
        }}
      />
      <ConfirmActionDialog
        open={Boolean(deletePaymentRequest)}
        title={
          deletePaymentRequest?.status === 'incassato'
            ? 'Stornare questo incasso?'
            : 'Annullare questo incasso?'
        }
        description="La fonte contabile verrà chiusa e la proiezione nel Calendario sarà aggiornata."
        confirmLabel={deletePaymentRequest?.status === 'incassato' ? 'Storna incasso' : 'Annulla incasso'}
        tone="danger"
        onCancel={() => {
          setDeletePaymentRequest(null);
          setSearchParams(clearEntityDeepLink(searchParams, ['paymentId']), { replace: true });
        }}
        onConfirm={async () => {
          const item = deletePaymentRequest;
          const result =
            item.status === 'incassato'
              ? await incomeData.reversePayment(item.id)
              : await incomeData.cancelPayment(item.id);
          if (result?.ok) {
            setDeletePaymentRequest(null);
            setSearchParams(clearEntityDeepLink(searchParams, ['paymentId']), { replace: true });
          }
        }}
      />
    </PageFrame>
  );
}
